import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { checkHealth } from '../../services/api';
import { getCompetitions } from '../../api/competitionApi';
import Hero from '../../components/Hero';
import CompetitionCard from '../../components/CompetitionCard';
import InternshipCard from '../../components/InternshipCard';
import {
  TrophyIcon,
  UserGroupIcon,
  StarIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const Home = () => {
  const [competitions, setCompetitions] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [greetingIndex, setGreetingIndex] = useState(0);

  const navigate = useNavigate();
  const showToast = useToast();
  const { user, loading: authLoading } = useAuth();

  const greetings = [
    "Welcome. Ready to participate?",
    "Explore verified skill challenges.",
    "Build your professional proof-of-work.",
    "Structured evaluation. Real outcomes.",
  ];

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Fetch competitions + rotate greeting
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        await checkHealth().catch(() => ({ status: 'down' }));
        const res = await getCompetitions();
        const data = Array.isArray(res)
          ? res
          : res?.competitions || res?.data || [];
        setCompetitions(data);
      } catch (err) {
        console.error('Home fetch error:', err);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();

    const interval = setInterval(() => {
      setGreetingIndex((prev) => (prev + 1) % greetings.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Show up to 3 active items (competitions + internships mixed)
  const liveItems = useMemo(() => {
    const now = new Date();
    const active = competitions.filter(
      (c) => !c.end_date || new Date(c.end_date) >= now
    );
    const list = active.length > 0 ? active : competitions;

    // Normalise type label for CompetitionCard display
    return list.slice(0, 3).map((c) => ({
      ...c,
      normalizedType: c.type
        ? c.type.charAt(0).toUpperCase() + c.type.slice(1).toLowerCase()
        : 'Competition',
    }));
  }, [competitions]);

  // While auth resolves or user is logged in — render nothing
  if (authLoading || user) return null;

  // ── onJoin for unauthenticated users — always redirect to login ──────────
  const handleJoin = () => {
    showToast("Please log in to participate.", "info");
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-blue-500/20">
      <Hero />

      {/* ── Platform Highlights ─────────────────────────────────────────────── */}
      <section className="py-16 border-y border-white/5 bg-black">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10 text-center">
          {[
            {
              icon: UserGroupIcon,
              title: "Growing Community",
              desc: "Skilled participants across domains",
            },
            {
              icon: TrophyIcon,
              title: "Merit-Based Evaluation",
              desc: "Structured and transparent review",
            },
            {
              icon: StarIcon,
              title: "Industry Alignment",
              desc: "Real-world skill validation focus",
            },
          ].map((item, i) => (
            <div key={i}>
              <item.icon className="h-6 w-6 mx-auto mb-4 text-blue-400 opacity-70" />
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Active Opportunities ─────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex justify-between items-end mb-10">
          <div>
            <h2 className="text-3xl font-bold">Active Opportunities</h2>
            <p className="text-gray-500 text-sm mt-2">
              Competitions and internship programs — open for enrollment.
            </p>
          </div>
          <button
            onClick={() => navigate('/competitions')}
            className="hidden md:flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
          >
            View All <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        {dataLoading ? (
          // Skeleton loaders
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[300px] bg-white/5 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        ) : liveItems.length === 0 ? (
          <div className="text-center py-20 text-gray-600 text-sm italic">
            No active opportunities right now. Check back soon.
          </div>
        ) : (
          // ✅ Route to correct card based on type
          // Both CompetitionCard and InternshipCard have identical prop signatures:
          //   data, onJoin, processingId, disabled
          // Internship type gets InternshipCard (shows duration, stipend, enroll)
          // Everything else gets CompetitionCard (shows prize, join)
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {liveItems.map((item) => {
              const isInternship = item.type?.toLowerCase() === 'internship';

              return isInternship ? (
                <InternshipCard
                  key={item.id}
                  data={item}
                  onJoin={handleJoin}
                  processingId={null}
                  disabled={false}
                />
              ) : (
                <CompetitionCard
                  key={item.id}
                  data={item}
                  onJoin={handleJoin}
                  processingId={null}
                  disabled={false}
                />
              );
            })}
          </div>
        )}

        {/* Mobile — View All */}
        <div className="mt-8 flex md:hidden justify-center">
          <button
            onClick={() => navigate('/competitions')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-white/10 px-5 py-2.5 rounded-xl transition"
          >
            View All <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className="py-10 text-center border-t border-white/5">
        <p className="text-xs text-gray-600">
          © 2026 The Arena. Structured skill evaluation platform.
        </p>
      </footer>

      {/* ── Rotating greeting badge ─────────────────────────────────────────── */}
      <div className="fixed bottom-6 left-6 z-40">
        <motion.div
          key={greetingIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="px-4 py-2 rounded-full border border-white/10 bg-black/80 text-xs text-gray-300"
        >
          {greetings[greetingIndex]}
        </motion.div>
      </div>
    </div>
  );
};

export default Home;